import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TripadvisorActivityCardData } from './tourActivityTypes';
import { Modal } from './Modal';

interface TourActivityCardProps {
  data: TripadvisorActivityCardData | TripadvisorActivityCardData[];
}

export const TourActivityCard: React.FC<TourActivityCardProps> = ({ data }) => {
  const { t } = useTranslation();
  const activities = Array.isArray(data) ? data : [data];

  return (
    <div className="tour-activity-cards-wrapper">
      <div className="cards-section-header">
        <h2 className="cards-section-title">{t('tourActivityCard.sectionTitle')}</h2>
      </div>
      <div className="tour-activity-cards-container">
        {activities.map((activity) => <ActivityCardItem key={activity.id} activity={activity} />)}
      </div>
    </div>
  );
};

const ActivityCardItem: React.FC<{ activity: TripadvisorActivityCardData }> = ({ activity }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);

  return (
    <div className="tour-activity-card-wrapper">
      <div
        className="tour-activity-card"
        onClick={openModal}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') openModal();
        }}
        role="button"
        tabIndex={0}
      >
        {activity.images[0] && (
          <div className="tour-activity-image">
            <img src={getTripadvisorDisplayImage(activity.images[0])} alt={activity.name} />
          </div>
        )}
        <div className="tour-activity-card-header">
          <div className="tour-activity-info">
            <h3 className="tour-activity-name">{activity.name}</h3>
            {activity.location && <span>{activity.location}</span>}
            {activity.rating !== undefined && (
              <div className="tour-activity-rating">
                <span>★ {activity.rating}{activity.reviews ? ` (${activity.reviews})` : ''}</span>
              </div>
            )}
          </div>
          {activity.price && (
            <div className="tour-activity-price"><span className="price-amount">{activity.price}</span></div>
          )}
        </div>
        {activity.description && <div className="tour-activity-short-desc"><p>{activity.description}</p></div>}
        {activity.duration && <div className="tour-activity-duration"><span>{activity.duration}</span></div>}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={activity.name}>
        <div className="tour-activity-modal-content">
          {activity.images.length > 0 && (
            <section className="details-section gallery-section">
              <h3>{t('tourActivityCard.gallery')}</h3>
              <ActivityImageGallery images={activity.images} activityName={activity.name} />
            </section>
          )}

          {activity.description && (
            <section className="details-section">
              <h3>{t('tourActivityCard.description')}</h3>
              <p className="full-description">{activity.description}</p>
            </section>
          )}

          <section className="details-section">
            <h3>{t('tourActivityCard.activityDetails')}</h3>
            <div className="details-grid">
              {activity.price && <Detail label={t('tourActivityCard.price')} value={activity.price} />}
              {activity.duration && <Detail label={t('tourActivityCard.minimumDuration')} value={activity.duration} />}
              {activity.startTime && <Detail label={t('tourActivityCard.startTime')} value={activity.startTime} />}
              {activity.tourLanguage && <Detail label={t('tourActivityCard.language')} value={activity.tourLanguage} />}
              {activity.requirements && <Detail label={t('tourActivityCard.requirements')} value={activity.requirements} />}
              {activity.recommendation && <Detail label={t('tourActivityCard.recommendation')} value={activity.recommendation} />}
            </div>
            <p>{t('tourActivityCard.priceDisclaimer')}</p>
          </section>

          {activity.keyDetails.length > 0 && (
            <section className="details-section">
              <h3>{t('tourActivityCard.keyDetails')}</h3>
              <ul>{activity.keyDetails.map((detail) => <li key={detail}>{detail}</li>)}</ul>
            </section>
          )}

          {activity.priceOptions?.ageBands.length ? (
            <section className="details-section">
              <h3>{t('tourActivityCard.agePrices')}</h3>
              {activity.priceOptions.ageBands.map((band, index) => (
                <p key={`${band.type}-${index}`}>
                  {band.type || 'Traveler'} {band.minAge !== undefined && band.maxAge !== undefined ? `(${band.minAge}-${band.maxAge})` : ''}: {band.price ?? '—'}
                </p>
              ))}
            </section>
          ) : null}

          {activity.additionalInfo.map((info, index) => info.title && info.details ? (
            <section className="details-section" key={`${info.title}-${index}`}>
              <h3>{info.title}</h3>
              <p className="full-description">{info.details}</p>
            </section>
          ) : null)}

          {activity.link && <a className="card-external-link" href={activity.link} target="_blank" rel="noreferrer">{t('tourActivityCard.externalLink')}</a>}
        </div>
      </Modal>
    </div>
  );
};

const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value}</span>
  </div>
);

const ActivityImageGallery: React.FC<{ images: string[]; activityName: string }> = ({ images, activityName }) => {
  const galleryImages = images.slice(0, 6).map(getTripadvisorDisplayImage);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const showPrevious = () => {
    setSelectedImageIndex((current) => current === null ? null : (current - 1 + galleryImages.length) % galleryImages.length);
  };
  const showNext = () => {
    setSelectedImageIndex((current) => current === null ? null : (current + 1) % galleryImages.length);
  };

  return (
    <>
      <div className="gallery-grid">
        {galleryImages.map((image, index) => (
          <button
            className="gallery-thumbnail"
            key={image}
            onClick={() => setSelectedImageIndex(index)}
            type="button"
            aria-label={`${activityName} ${index + 1}`}
          >
            <img src={image} alt={`${activityName} ${index + 1}`} />
          </button>
        ))}
      </div>

      {selectedImageIndex !== null && (
        <div
          className="gallery-lightbox"
          onClick={() => setSelectedImageIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label={activityName}
        >
          <button
            className="lightbox-close"
            onClick={() => setSelectedImageIndex(null)}
            type="button"
            aria-label="Close"
          >
            ×
          </button>
          {galleryImages.length > 1 && (
            <>
              <button
                className="lightbox-nav lightbox-prev"
                onClick={(event) => { event.stopPropagation(); showPrevious(); }}
                type="button"
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="lightbox-nav lightbox-next"
                onClick={(event) => { event.stopPropagation(); showNext(); }}
                type="button"
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}
          <div className="lightbox-content" onClick={(event) => event.stopPropagation()}>
            <img src={galleryImages[selectedImageIndex]} alt={`${activityName} ${selectedImageIndex + 1}`} />
            <span className="lightbox-counter">{selectedImageIndex + 1} / {galleryImages.length}</span>
          </div>
        </div>
      )}
    </>
  );
};

function getTripadvisorDisplayImage(imageUrl: string): string {
  return imageUrl.replace(/attractions-splice-spp-75x75/i, 'attractions-splice-spp-720x480');
}
